import moo, { Lexer } from 'moo';
import { Token, AllowedTokens } from './TokenList.tsx';

const getLexer = ({ allowedVariables = [], allowedFunctions = [], allowedOperators = [] }: AllowedTokens) =>
    moo.compile({
        ws: /[ \t]+/,
        operator: allowedOperators,
        variable: allowedVariables,
        functionName: allowedFunctions.map((f) => f.name),
        lparen: '(',
        rparen: ')',
        string: [/"(?:[^"])*"?/, /'(?:[^'])*'?/],
        number: /\d+(?:\.\d+)?/,
        word: /[A-Za-z_][A-Za-z0-9_]*/,
    });

export const parseExpression = (
    lexer: Lexer,
    text: string,
    allowedFunctions: { name: string; slotCount: number }[] = []
): Token[] => {
    lexer.reset(text);
    const root: Token[] = [];
    const stack: Token[][] = [root];
    let lastToken: Token | null = null;

    for (const tok of lexer) {
        if (tok.type === 'ws') continue;
        const current = stack[stack.length - 1];

        if (tok.type === 'functionName') {
            const fn: Token = {
                type: 'function',
                value: tok.value,
                slotCount: allowedFunctions.find((f) => f.name === tok.value)?.slotCount ?? 1,
                tokens: [],
            };
            current.push(fn);
            lastToken = fn;
            continue;
        }

        if (tok.type === 'lparen') {
            // if last token was a function, its tokens become current
            if (lastToken?.type === 'function') {
                stack.push(lastToken.tokens!);
            } else {
                const group: Token = { type: 'group', value: '()', tokens: [] };
                current.push(group);
                stack.push(group.tokens!);
            }
            lastToken = null;
            continue;
        }

        if (tok.type === 'rparen') {
            if (stack.length > 1) stack.pop();
            lastToken = null;
            continue;
        }

        let token: Token;
        if (tok.type === 'variable') {
            token = { type: 'variable', value: tok.value };
        } else if (tok.type === 'operator') {
            token = { type: 'operator', value: tok.value };
        } else {
            token = { type: 'constant', value: tok.value };
        }

        current.push(token);
        lastToken = token;
    }

    // close any unclosed groups/functions gracefully
    while (stack.length > 1) stack.pop();

    return root;
};

const serializeExpression = (tokens: Token[]): string => {
    return tokens
        .map((token) => {
            if (token.type === 'function') {
                const args = token.tokens?.map((t) => (t ? serializeExpression([t]) : '')).join(', ') || '';
                return `${token.value}(${args})`;
            }
            if (token.type === 'group') {
                return `(${serializeExpression((token.tokens?.filter((t) => t !== null) as Token[]) || [])})`;
            }
            if (token.type === 'constant' && /\s/.test(token.value)) {
                return `"${token.value}"`;
            }
            return token.value;
        })
        .join(' ');
};

export default ({ allowedVariables = [], allowedFunctions = [], allowedOperators = [] }: AllowedTokens) => {
    const lexer = getLexer({ allowedVariables, allowedFunctions, allowedOperators });
    return {
        parse: (text: string) => parseExpression(lexer, text, allowedFunctions),
        serialize: (tokens: Token[]) => serializeExpression(tokens),
    };
};
